import { router } from '@inertiajs/core';
import classNames from 'classnames';
import { ReactNode, useEffect } from 'react';
import { Download } from 'react-feather';
import { useTranslation } from 'react-i18next';

import { useLocale } from '../../Shared/locale';
import { Card } from '../../Shared/UI/Card';
import { Icon } from '../../Shared/UI/Icon';
import { Spinner } from '../../Shared/UI/Spinner';
import { SharedProps } from '../types';
import Container from '../UI/Container';
import Header from '../UI/Header';
import HeaderTitle from '../UI/HeaderTitle';
import Layout from '../UI/Layout';

interface Export {
  id: string;
  filename: string;
  download_url: string | null;
  progress: number | null;
  failed: boolean;
  created_at: string;
}

export interface ExportsPageProps extends SharedProps {
  exports: Export[];
}

export default function ExportsPage({ exports }: ExportsPageProps) {
  const { t } = useTranslation();
  const hasPendingExports = exports.some((item) => !item.failed && !item.download_url);

  useEffect(() => {
    if (!hasPendingExports) {
      return;
    }

    const timer = window.setInterval(() => {
      router.reload({ only: ['exports'] });
    }, 2_000);

    return () => window.clearInterval(timer);
  }, [hasPendingExports]);

  return (
    <>
      <Header>
        <Container size="md">
          <HeaderTitle>
            {t('backend:exports')}
          </HeaderTitle>
          {t('backend:recent_exports_description')}
        </Container>
      </Header>

      <Container size="md">
        <div className="space-y-2">
          {exports.length === 0 && (
            <Card className="text-slate-500">
              {t('backend:no_exports_yet')}
            </Card>
          )}
          {exports.map((item) => (
            <ExportCard exportItem={item} key={item.id} />
          ))}
        </div>
      </Container>
    </>
  );
}

function ExportCard({
  exportItem,
}: {
  exportItem: Export;
}) {
  const { t } = useTranslation();
  const { formatDate } = useLocale();

  const isFailed = exportItem.failed;
  const isReady = !isFailed && !!exportItem.download_url;
  const isPending = !isFailed && !isReady;
  const muted = !exportItem.download_url && 'opacity-50';

  return (
    <Card
      spacing="xs"
      link={exportItem.download_url ? {
        href: exportItem.download_url,
        ariaLabel: exportItem.filename,
      } : undefined}
    >
      <div className="grid grid-cols-[2fr_80px_150px] gap-4 items-center">
        <div className={classNames(muted, 'min-w-0 leading-snug')}>
          <h4 className="whitespace-nowrap overflow-hidden text-ellipsis">
            {isPending ? (
              <Icon className="mr-1" >
                <Spinner />
              </Icon>
            ) : (
              <Icon className="mr-1">
                <Download />
              </Icon>
            )}
            {exportItem.filename}
          </h4>
        </div>
        <div className="text-sm font-medium">
          {isPending && (
            <>
              {exportItem.progress || 0}%
            </>
          )}
          {exportItem.download_url && (
            <span className="text-emerald-600">
              {t('backend:export_ready')}
            </span>
          )}
          {isFailed && (
            <span className="text-red-600">
              {t('backend:export_failed')}
            </span>
          )}
        </div>
        <div className="text-slate-400 text-right text-sm">
          {formatDate(exportItem.created_at, 'display_date_time')}
        </div>
      </div>
    </Card>
  );
}

ExportsPage.layout = (page: ReactNode) => <Layout>{page}</Layout>;
